import { type SubmitEvent as ReactSubmitEvent, Fragment, memo, useState, type PropsWithChildren } from "react";
import {
	Button, ButtonTypes, Checkbox, Icon, Input, Modal, Select, type SelectComponent
} from "@clintonelec/react-storybook";
import type { User } from "Interfaces";
import "Components/ConfigLayout/User/UserManagement/UserManagementModal/UserManagementModal.less";
import { groupSelectOptions } from "Data/Objects/User";
import UserValidation from "Components/ConfigLayout/User/UserValidation";
import { idLengthRegex, passwordRegex } from "Data/Utils/User";
import { v4 as uuidv4 } from "uuid";

type UserManagementForm = {
	confirmPassword: HTMLInputElement;
	email: HTMLInputElement;
	group: SelectComponent;
	newPassword: HTMLInputElement;
	notifications: HTMLInputElement;
	userId: HTMLInputElement;
} & HTMLFormElement;

type UserManagementModalProps = {
	user?: User;
	allUsers?: User[];
	onSubmit: (newUser?: User) => void;
};

function UserManagementModal(props: PropsWithChildren<UserManagementModalProps>) {
	const { children, user, allUsers, onSubmit } = props;
	const [ userId, setUserId ] = useState(user?.userId ?? "");
	const [ password, setPassword ] = useState("");
	const [ oldPassword, setOldPassword ] = useState("");
	const [ submitted, setSubmitted ] = useState(false);
	const [ visible, setVisible ] = useState(false);

	const handleVisibilityChange = (newVisibilty: boolean) => {
		setSubmitted(false);
		setPassword("");
		setOldPassword("");
		setVisible(newVisibilty);
	};

	const handleFormSubmit = (event: ReactSubmitEvent<UserManagementForm>) => {
		setSubmitted(true);
		event.preventDefault();
		event.stopPropagation();

		if (event.currentTarget?.checkValidity()) {
			const { group, email, notifications } = event.currentTarget;
			let updatedUser: User;

			if (user) {
				updatedUser = {
					...user,
					userId,
					email: email.value,
					group: group.value,
					notifications: notifications.checked
				};
			} else {
				updatedUser = {
					id: uuidv4(),
					userId,
					email: email.value,
					group: group.value,
					notifications: notifications.checked
				};
			}

			onSubmit(updatedUser);
			setVisible(!visible);
		}
	};

	const handleUserIdUpdate = (newId: string) => setUserId(newId);

	const checkDuplicateUserId = (newUserId: string) => {
		const allUserIds = allUsers?.map((currUser => currUser.userId));

		return new Set(allUserIds).size !== new Set([ ...(allUserIds ?? []), newUserId ]).size;
	};

	const userIdValidityMessage = () => {
		if(!checkDuplicateUserId(userId)) {
			return "The User ID is already being used, please select a different name.";
		}
	};

	const validateUserId = (value?: string | number | readonly string[]) => {
		if (typeof value !== "string") {
			return false;
		}

		return idLengthRegex.test(value) && checkDuplicateUserId(value);
	};

	const handleOldPasswordUpdate = (newpassword: string) => setOldPassword(newpassword);
	const handlePasswordUpdate = (newPassword: string) => setPassword(newPassword);

	const validatePassword = (value?: string | number | readonly string[]) => {
		if (typeof value !== "string") {
			return false;
		}

		return passwordRegex.test(value);
	};

	const validateConfirmPassword = (inputValue?: string | number | readonly string[]) => {
		return inputValue === password;
	};

	const renderTitle = () => {
		const titleText = user ? "Edit User" : "Create User";

		return (
			<Fragment>
				<Icon name="user" />
				{ titleText }
			</Fragment>
		);
	};

	const renderPasswordFields = () => {
		const passwordFields = (
			<Fragment>
				<Input
					disabled={ user && oldPassword.length === 0 }
					name="newPassword"
					noValidate={ !submitted }
					onUpdate={ handlePasswordUpdate }
					password
					placeholder="Password"
					required={ !user }
					validator={ validatePassword }
					autoComplete="new-password"
				/>
				<Input
					disabled={ user && oldPassword.length === 0 }
					name="confirmPassword"
					noValidate={ !submitted }
					password
					placeholder="Confirm Password"
					required={ password.length > 0 }
					validator={ validateConfirmPassword }
				/>
			</Fragment>
		);

		if (!user) {
			return passwordFields;
		}

		return (
			<Fragment>
				<Input
					name="oldPassword"
					password
					placeholder="Old Password"
					onUpdate={ handleOldPasswordUpdate }
				/>
				{ passwordFields }
			</Fragment>
		);
	};

	const modalContent = (
		<form
			className="user-management-modal-content"
			noValidate
			onSubmit={ handleFormSubmit }
		>
			<div className="scrollable">
				<label>User ID</label>
				<Input
					autoComplete="new-password"
					defaultValue={ user?.userId }
					name="userId"
					noValidate={ !submitted }
					onUpdate={ handleUserIdUpdate }
					required
					validator={ validateUserId }
					validityMessage={ userIdValidityMessage() }
				/>
				<label>Email</label>
				<Input
					value={ user?.email }
					name="email"
					noValidate={ !submitted }
				/>
				<label>Group</label>
				<Select
					allowClear= { false }
					defaultValue={ user?.group ?? "USER" }
					name="group"
					options={ groupSelectOptions }
				/>
				<label>Password</label>
				{ renderPasswordFields() }
				<Checkbox name="notifications" defaultChecked={ user?.notifications }>
					Enable Notifications
				</Checkbox>
				<UserValidation
					isDuplicateUserId={ checkDuplicateUserId(userId) }
					password={ password }
					userId={ userId }
				/>
			</div>
			<div className="footer">
				<Button type={ ButtonTypes.TERTIARY }>
					Submit
				</Button>
			</div>
		</form>
	);

	return (
		<Modal
			className="user-management-modal"
			modalContent={ modalContent }
			onVisibilityChange={ handleVisibilityChange }
			title={ renderTitle() }
			visible={ visible }
			width={ 700 }
		>
			{ children }
		</Modal>
	);
}

export default memo(UserManagementModal);
